import clsx from 'clsx';
import React, { useEffect, useState } from 'react';
import { HiChevronDown } from 'react-icons/hi';
import { GroupBase } from 'react-select';
import Select, { AsyncProps } from 'react-select/async';
import { FormLabel } from '../FormLabel';
import { useTheme, withTheme } from '../ThemeProvider';
import { StyledAsyncSelectContainer, StyledErrorMessage, getSelectStyles } from './styles';

export interface IAsyncSelect extends AsyncProps<unknown, boolean, GroupBase<unknown>> {
  label?: string | React.ReactNode;
  error?: string;
  required?: boolean;
  rootClassNames?: string;
  help?: string;
}

const AsyncSelectComponent = React.forwardRef<any, IAsyncSelect>((props: IAsyncSelect, ref) => {
  const { label, required, error, help, styles, ...rest } = props;
  const { theme } = useTheme();
  const [selectStyles, setSelectStyles] = useState<any>(null);

  useEffect(() => {
    // Get styles once the component is mounted to ensure theme is available
    setSelectStyles(getSelectStyles(theme));
  }, [theme]);

  // If styles aren't ready yet, don't render the select component
  if (!selectStyles) {
    return <div>Loading...</div>;
  }

  return (
    <StyledAsyncSelectContainer className="flex min-w-40 flex-col">
      {label && (
        <div className="mb-2.5">
          <FormLabel label={label} help={help} required={required} />
        </div>
      )}
      <Select
        {...rest}
        placeholder={<p className="text-blue text-[16px] font-normal">{props.placeholder}</p>}
        menuPortalTarget={document.body}
        styles={
          {
            ...selectStyles,
            ...styles,
          } as any
        }
        unstyled
        defaultOptions
        cacheOptions
        menuShouldScrollIntoView={false}
        className={clsx(
          'w-full',
          props.rootClassNames,
          props.isDisabled && '!opacity-50 !bg-disabled',
        )}
        aria-invalid={!!error}
        ref={ref}
        defaultValue={rest.defaultValue}
        components={{
          ...(rest.components || {}),
          DropdownIndicator: () => <HiChevronDown size={16} />,
        }}
      />
      {error && <StyledErrorMessage>{error}</StyledErrorMessage>}
    </StyledAsyncSelectContainer>
  );
});

AsyncSelectComponent.displayName = 'AsyncSelect';

export const AsyncSelect = withTheme(AsyncSelectComponent);
